"use client";

import * as React from "react";
import { format, addMonths } from "date-fns";
import { CalendarIcon, X, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/Calendar";
import { DateRange } from "react-day-picker";
import { holidayDates } from "@/lib/holidays";
import { useSearchParams } from "next/navigation";
import { HotelService, RoomAvailability } from "@/services/api";

interface DatePickerWithRangeProps {
  className?: string;
  from: Date;
  to: Date;
  onChange: (range: { from: Date; to: Date }) => void;
}

export function DatePickerWithRange({
  className,
  from,
  to,
  onChange,
}: DatePickerWithRangeProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [selectedRange, setSelectedRange] = React.useState<DateRange | undefined>({
    from,
    to,
  });
  const [isMobile, setIsMobile] = React.useState(false);

  const searchParams = useSearchParams();
  const db = searchParams.get("db");
  const hotelId = searchParams.get("hotel_id");
  const [prices, setPrices] = React.useState<Record<string, number>>({});

  // Detect mobile screen size
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  React.useEffect(() => {
    async function fetchPrices() {
      if (!db || !hotelId) return;

      const startDate = new Date();
      const endDate = addMonths(startDate, 12);

      try {
        const data = await HotelService.checkAvailability(db, {
          checkIn: format(startDate, "yyyy-MM-dd"),
          checkOut: format(endDate, "yyyy-MM-dd"),
          adults: 1,
          children: 0,
          room: 1,
        });

        const priceMap: Record<string, number> = {};

        if (data && data.data) {
          data.data.forEach((room: RoomAvailability) => {
            if (room.availability && room.availability.dates) {
              room.availability.dates.forEach((dateInfo) => {
                const dateStr = dateInfo.date;
                const price = dateInfo.final_rate;

                if (price > 0) {
                  if (!priceMap[dateStr] || price < priceMap[dateStr]) {
                    priceMap[dateStr] = price;
                  }
                }
              });
            }
          });
        }

        console.log("Prices map:", priceMap);
        setPrices(priceMap);
      } catch (error) {
        console.error("Failed to fetch prices for calendar", error);
      }
    }

    if (isOpen) {
      fetchPrices();
    }
  }, [isOpen, db, hotelId]);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [containerRef]);

  // Sync internal state if props change
  React.useEffect(() => {
    setSelectedRange({ from, to });
  }, [from, to]);

  const handleSelect = (range: DateRange | undefined) => {
    setSelectedRange(range);

    // Only update parent when we have BOTH dates selected (2-click flow)
    if (range?.from && range?.to) {
      onChange({ from: range.from, to: range.to });
    }
  };

  // Calculate nights
  const nights = selectedRange?.from && selectedRange?.to
    ? Math.round((selectedRange.to.getTime() - selectedRange.from.getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className={cn("relative grid gap-2", className)} ref={containerRef}>
      {/* Trigger Button */}
      <div
        className={cn(
          "w-full justify-start text-left font-normal flex items-center px-4 h-12 border border-slate-200 rounded-2xl bg-white cursor-pointer hover:border-slate-300 transition-all duration-300 text-slate-900 font-sans group",
          !from && "text-muted-foreground",
          className
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="mr-3 h-8 w-8 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-slate-100 transition-all duration-300 shrink-0">
          <CalendarIcon className="h-4 w-4 text-slate-500" />
        </div>
        <div className="flex-1 min-w-0">
          {from ? (
            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">Check-in</span>
                <span className="text-sm font-medium text-slate-900 truncate">{format(from, isMobile ? "MMM dd, yy" : "EEE, MMM dd")}</span>
              </div>
              {to && (
                <>
                  <div className="h-8 w-px bg-slate-100 mx-1 shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">Check-out</span>
                    <span className="text-sm font-medium text-slate-900 truncate">{format(to, isMobile ? "MMM dd, yy" : "EEE, MMM dd")}</span>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col">
              <span className="text-sm font-medium text-slate-700">Select dates</span>
              <span className="text-xs text-slate-400">Choose your check-in and check-out</span>
            </div>
          )}
        </div>
        {from && (
          <div className="ml-2 h-7 w-7 bg-emerald-50 rounded-full flex items-center justify-center shrink-0">
            <Check className="h-3.5 w-3.5 text-emerald-600" />
          </div>
        )}
      </div>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={() => setIsOpen(false)}
          />
      
          {/* Modal Content - Compact */}
          <div className="relative z-50 w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header - Minimal */}
            <div className="bg-white border-b border-gray-100 px-3 sm:px-4 py-2 sm:py-3 shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-indigo-600" />
                  <h3 className="font-semibold text-sm text-gray-800">Select Dates</h3>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="h-6 w-6 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                >
                  <X className="h-3.5 w-3.5 text-gray-400" />
                </button>
              </div>
      
              {/* Progress Steps - Compact */}
              <div className="flex items-center gap-2 mt-2">
                <div className={cn(
                  "flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs transition-all",
                  selectedRange?.from
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-gray-100 text-gray-400"
                )}>
                  <div className={cn(
                    "h-4 w-4 rounded-full flex items-center justify-center text-[10px] font-semibold",
                    selectedRange?.from ? "bg-indigo-600 text-white" : "bg-gray-300 text-white"
                  )}>
                    1
                  </div>
                  <span>Check-in</span>
                </div>
      
                <div className={cn(
                  "flex-1 h-px transition-all",
                  selectedRange?.from ? "bg-indigo-200" : "bg-gray-200"
                )} />
      
                <div className={cn(
                  "flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs transition-all",
                  selectedRange?.from && selectedRange?.to
                    ? "bg-indigo-100 text-indigo-700"
                    : selectedRange?.from
                      ? "bg-amber-50 text-amber-600"
                      : "bg-gray-100 text-gray-400"
                )}>
                  <div className={cn(
                    "h-4 w-4 rounded-full flex items-center justify-center text-[10px] font-semibold",
                    selectedRange?.to ? "bg-indigo-600 text-white" : "bg-gray-300 text-white"
                  )}>
                    2
                  </div>
                  <span>Check-out</span>
                </div>
              </div>
            </div>
      
            {/* Calendar Container */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-2 sm:p-3">
              <Calendar
                mode="range"
                defaultMonth={from}
                selected={selectedRange}
                onSelect={handleSelect}
                numberOfMonths={2}
                className="p-0 shadow-none border-0"
                modifiers={{ holiday: holidayDates }}
                modifiersClassNames={{ holiday: "text-red-500 font-medium" }}
                prices={prices}
              />
            </div>
      
            {/* Footer - Compact */}
            <div className="flex items-center justify-between p-2 sm:p-3 border-t border-gray-100 bg-white shrink-0">
              <div className="flex items-center gap-2">
                {selectedRange?.from && (
                  <>
                    <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-lg">
                      <CalendarIcon className="h-3 w-3 text-gray-400" />
                      <span className="text-xs font-medium text-gray-700">
                        {format(selectedRange.from, isMobile ? "MMM dd" : "MMM dd")}
                        {selectedRange.to && ` – ${format(selectedRange.to, isMobile ? "MMM dd" : "MMM dd")}`}
                      </span>
                    </div>
                    {selectedRange.to && (
                      <div className="flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-lg">
                        <span className="text-xs font-semibold text-emerald-600">{nights} night{nights > 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </>
                )}
                {!selectedRange?.from && (
                  <span className="text-xs text-gray-400">Select a date to begin</span>
                )}
              </div>
      
              <button
                onClick={() => setIsOpen(false)}
                disabled={!selectedRange?.from}
                className={cn(
                  "px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs font-semibold transition-all",
                  selectedRange?.from
                    ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                )}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
